import React, { useState, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import DashboardLayout from '../layout/DashboardLayout';
import Dashboard from '../pages/Dashboard';

import Login from '../pages/Login';

import Logout from '../pages/Logout';

import InvoiceCreator from '../pages/InvoiceCreator';
import CompanyPage from '../pages/Company';
import AddCompany from '../pages/AddCompany';
import Customer from '../pages/Customer';
import AddCustomer from '../pages/AddCustomer';
import Settings from '../pages/Settings';
import AddBank from '../pages/AddBank';
import Invoice from '../pages/Invoice';
import Editinvoice from '../pages/EditInvoice';
import SubscriptionBilling from '../pages/SubscriptionBilling';

// New Pages
import PurchaseInvoices from '../pages/PurchaseInvoices';
import PurchaseInvoiceCreator from '../pages/PurchaseInvoiceCreator';
import GstReport from '../pages/GstReport';
import RecurringInvoices from '../pages/RecurringInvoices';
import StatementUpload from '../pages/StatementUpload';
import TdsReport from '../pages/TdsReport';
import Reports from '../pages/Reports';
import PartyLedger from '../pages/PartyLedger';
import Services from '../pages/Services';
import CustomerLogin from '../pages/CustomerLogin';
import CustomerPortal from '../pages/CustomerPortal';
import Showcase from '../pages/Showcase';
import Quotation from '../pages/Quotation';
import QuotationCreator from '../pages/QuotationCreator';
import Contract from '../pages/Contract';
import ContractCreator from '../pages/ContractCreator';
import Vendor from '../pages/Vendor';
import AddVendor from '../pages/AddVendor';
import InventoryDashboard from '../pages/InventoryDashboard';
import PosTerminal from '../pages/PosTerminal';
import CreditNotesList from '../pages/CreditNotes';
import DebitNotesList from '../pages/DebitNotes';


// Preloader component — same as before, no change
const Preloader = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#E1EAFF] via-[#fef6e4] to-[#ddffe6]">
    <div className="max-w-md w-full px-10 py-10 bg-white/95 rounded-3xl shadow-2xl flex flex-col items-center text-center border-t-4 border-b-4 border-[#7B5AFF] animate-fade-in-alt relative">
      <div className="mb-7 mt-1">
        <div className="mx-auto mb-3 flex items-center justify-center">
          <div className="relative">
            <svg className="animate-spin-gear h-12 w-12 text-[#7B5AFF] drop-shadow-lg" fill="none" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="18" stroke="#34BFA3" strokeWidth="4" opacity="0.19"/>
              <circle cx="24" cy="24" r="11" stroke="#FFB400" strokeWidth="3" strokeDasharray="51,19" />
              <rect x="20" y="5" width="8" height="8" rx="2" fill="#FF6680" className="animate-bounce-alt" />
            </svg>
            <span className="absolute bottom-[-12px] right-[-10px] animate-pop">
              <span role="img" aria-label="paper-plane" className="text-lg">🧾</span>
            </span>
          </div>
        </div>
        <div className="font-extrabold text-2xl tracking-tight text-[#7B5AFF]">
          Invoice Generator
        </div>
        <div className="font-bold text-[#FF6680] tracking-wider text-md mt-0.5">
          Setting up your invoice workspace...
        </div>
      </div>
      <div className="mt-7 flex flex-col items-center gap-5 hidden">
        {/* Basic messages/graphs hidden */}
      </div>
      <div className="mt-6 flex items-center justify-center gap-3 text-lg" aria-hidden>
        <span title="Generating Invoice"><span role="img" aria-label="file">📄</span></span>
        <span title="Calculating Totals"><span role="img" aria-label="calculator">🧮</span></span>
        <span title="Saving Progress"><span role="img" aria-label="floppy">💾</span></span>
        <span title="Securing Data"><span role="img" aria-label="lock">🔒</span></span>
      </div>
    </div>
    <style>{`
      @keyframes fade-in-alt { from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: scale(1); } }
      .animate-fade-in-alt { animation: fade-in-alt 0.5s cubic-bezier(.25,1,.5,1); }
      @keyframes spin-gear { 100% { transform: rotate(360deg);} }
      .animate-spin-gear { animation: spin-gear 1.25s linear infinite;}
      @keyframes bounce-alt {
        0%,100% { transform: translateY(1px);}
        50% { transform: translateY(-6px);}
      }
      .animate-bounce-alt {
        animation: bounce-alt 1.4s linear infinite;
      }
      @keyframes pop { 0% { transform: scale(.6);} 60% {transform:scale(1.12);} 100%{transform:scale(1);} }
      .animate-pop { animation: pop 0.6s .3s cubic-bezier(.28,.75,.68,1.75) both; }
    `}</style>
  </div>
);

// Custom preloader hook for the /add-party route: fetches party data before proceeding.
function useAddPartyPreloader() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [partyData, setPartyData] = useState<null | object>(null);
  const [delayDone, setDelayDone] = useState(false);

  useEffect(() => {
    // Only trigger on the "/add-party" route
    if (location.pathname === "/add-party") {
      setLoading(true);
      setDelayDone(false);

      // Trigger a 450ms timer in parallel with fetch
      const delayTimer = setTimeout(() => setDelayDone(true), 450);

      // Fetch from the backend before showing add-party page
      fetch("https://stockaadvikbackend.aadviktech.com/auth/addparty", {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(res => res.json())
        .then(data => {
          setPartyData(data);
        })
        .catch(() => {
          setPartyData({});
        });

      return () => {
        clearTimeout(delayTimer);
      };
    } else {
      // If not on /add-party route, reset
      setLoading(false);
      setDelayDone(false);
      setPartyData(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/add-party" && partyData !== null && delayDone) {
      // Both API and delay finished
      setLoading(false);
    } else if (location.pathname === "/add-party") {
      setLoading(true);
    }
  }, [partyData, delayDone, location.pathname]);

  return loading;
}

// Default page change preloader for all other pages
function usePagePreloader(delay = 400) {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't trigger for /add-party (handled by useAddPartyPreloader)
    if (location.pathname === "/add-party") {
      setLoading(false);
      return;
    }

    setLoading(true);
    const t = setTimeout(() => setLoading(false), delay);
    return () => clearTimeout(t);
  }, [location, delay]);

  return loading;
}

const ROUTE_PERMISSION_MAP: Record<string, string> = {
  "/pos": "pos",
  "/customer": "customer",
  "/addcustomer": "customer",
  "/vendors": "vendors",
  "/addvendor": "vendors",
  "/InvoiceCreator": "invoice-creator",
  "/editinvoice": "invoice-creator",
  "/Invoices": "invoices",
  "/purchase-invoices": "purchase-bills",
  "/purchase-invoice-creator": "record-purchase",
  "/recurring-invoices": "recurring",
  "/statement-upload": "upload-statement",
  "/reports": "reports",
  "/gst-report": "reports",
  "/tds-report": "reports",
  "/party-ledger": "party-ledger",
  "/services": "it-services",
  "/quotations": "quotations",
  "/quotation-creator": "quotations",
  "/contracts": "contracts",
  "/contract-creator": "contracts",
  "/credit-notes": "credit-notes",
  "/debit-notes": "debit-notes",
  "/inventory": "inventory"
};

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const path = location.pathname;
  const permissionKey = ROUTE_PERMISSION_MAP[path];
  
  if (permissionKey && role !== 'super-admin' && role !== 'company') {
    const permissionsStr = localStorage.getItem("permissions");
    if (permissionsStr) {
      try {
        const userPermissions: string[] = JSON.parse(permissionsStr);
        if (!userPermissions.includes(permissionKey)) {
          return <Navigate to="/" replace />;
        }
      } catch (e) {}
    }
  }

  return children;
};

interface Props { }

function AllRoutes(props: Props) {
  // If on "/add-party", use the custom data-fetching preloader, else use default
  const addPartyPreloading = useAddPartyPreloader();
  const pagePreloading = usePagePreloader(450);
  const showPreloader = addPartyPreloading || pagePreloading;

  return (
    <>
      {showPreloader && <Preloader />}
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/showcase" element={<Showcase />} />


        {/* Protected Routes */}
        <Route element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route path="/" element={<Dashboard />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/addcompany" element={<AddCompany />} />
          <Route path="/customer" element={<Customer />} />
          <Route path="/addcustomer" element={<AddCustomer />} />
          <Route path="/vendors" element={<Vendor />} />
          <Route path="/addvendor" element={<AddVendor />} />
          
          <Route path="/InvoiceCreator" element={<InvoiceCreator />} />
          <Route path="/Invoices" element={<Invoice />} />
          <Route path="/addBank" element={<AddBank />} />
          <Route path="/setting" element={<Settings />} />
          <Route path="/billing" element={<SubscriptionBilling />} />
          <Route path="/editinvoice" element={<Editinvoice />} />
          
          {/* New Routes */}
          <Route path="/purchase-invoices" element={<PurchaseInvoices />} />
          <Route path="/purchase-invoice-creator" element={<PurchaseInvoiceCreator />} />
          <Route path="/gst-report" element={<GstReport />} />
          <Route path="/recurring-invoices" element={<RecurringInvoices />} />
          <Route path="/statement-upload" element={<StatementUpload />} />
          <Route path="/tds-report" element={<TdsReport />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/party-ledger" element={<PartyLedger />} />
          <Route path="/quotations" element={<Quotation />} />
          <Route path="/quotation-creator" element={<QuotationCreator />} />
          <Route path="/contracts" element={<Contract />} />
          <Route path="/contract-creator" element={<ContractCreator />} />
          <Route path="/inventory" element={<InventoryDashboard />} />
          <Route path="/pos" element={<PosTerminal />} />
          <Route path="/credit-notes" element={<CreditNotesList />} />
          <Route path="/debit-notes" element={<DebitNotesList />} />
        </Route>
        {/* Customer Routes */}
        <Route path="/customer/login" element={<CustomerLogin />} />
        <Route path="/customer/portal" element={<CustomerPortal />} />
        <Route path="/services" element={<Services />} />

        <Route path="/logout" element={<Logout />} />
      </Routes>
    </>
  )
}

export default AllRoutes
